import { useEffect, useState } from "react";
import axiosInstance from "../../utils/axios-instance";
import UseGraph from "../../utils/use-graph";
import formQueryUrl from "../../utils/form-query-url";
import {
  CartesianGrid,
  LineChart,
  XAxis,
  Line,
  ResponsiveContainer,
  Tooltip,
} from "recharts";

function transformStatsForChart(stats: any) {
  const { daily, cumulative } = stats;

  const order = [
    "day1",
    "day2",
    "retry",
    "day6",
    "day7",
    "day8",
    "day9",
    "day10",
    "day11",
    "day12",
    "day13",
    "day14",
    "day15",
    "day16",
    "day17",
    "day18",
    "day19",
    "day20",
    "day21",
    "day22_cancelled",
  ];

  return order.map((key) => ({
    day: key === "retry" ? "Retries (3-5)" : key.replace("day", "Day "),
    daily: daily?.[key] ?? 0,
    cumulative: cumulative?.[key] ?? null,
  }));
}

export const CollectionDayRate = () => {
  const [data, setData] = useState<Record<string, any>[]>([]);
  const { startDate, endDate, isRestarted, chaseType } = UseGraph();

  useEffect(() => {
    const GetData = async () => {
      try {
        const filtersArray = [
          startDate !== null && { startDate },
          endDate !== null && { endDate },
          chaseType !== "all" && { chaseType },
        ];

        const res = await axiosInstance.get(
          `/stats/collection-payment-details${formQueryUrl(filtersArray, "?")}`
        );

        setData(res.data.data);
      } catch (error) {
        console.log(error);
      }
    };

    GetData();
  }, [startDate, endDate, isRestarted, chaseType]);

  return (
    <div className="flex flex-col w-full h-full min-h-[480px] rounded-2xl px-6 py-5 bg-surface border border-edge shadow-card">
      <h1 className="text-[19px] text-heading-color font-semibold tracking-tight">
        Collections{" "}
      </h1>
      <p className="text-[13px] text-light-gray font-medium mt-1">
        Daily and cumulative rate, by chase day
      </p>

      <div className="flex items-center gap-5 mt-4 mb-2">
        <div className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full bg-[#6366f1]" />
          <span className="text-[13px] text-light-gray font-medium">Daily</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full bg-[#10b981]" />
          <span className="text-[13px] text-light-gray font-medium">
            Cumulative
          </span>
        </div>
      </div>

      <div className="w-full flex-1 min-h-[360px]">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={transformStatsForChart(data)}
            margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
          >
            <Tooltip
              cursor={{ stroke: "var(--color-border-edge-strong)" }}
              content={(props) => (
                <CustomToolTip
                  prop={props}
                  data={transformStatsForChart(data)}
                />
              )}
            />
            <CartesianGrid
              vertical={false}
              stroke="var(--color-border-edge)"
              strokeDasharray="4 4"
            />
            <XAxis
              dataKey="day"
              tickLine={false}
              axisLine={false}
              tickMargin={10}
              tick={{ fill: "var(--color-light-gray)", fontSize: 11 }}
            />
            <Line
              type="monotone"
              dataKey="daily"
              stroke="#6366f1"
              strokeWidth={2.5}
              dot={false}
              activeDot={{ r: 4, strokeWidth: 0 }}
            />
            <Line
              type="monotone"
              dataKey="cumulative"
              stroke="#10b981"
              strokeWidth={2.5}
              dot={false}
              activeDot={{ r: 4, strokeWidth: 0 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const CustomToolTip = ({ prop, data }) => {
  const { label: labelData } = prop;
  let day = Number(labelData?.split(" ").pop());
  let label =
    labelData === "Day 22_cancelled"
      ? 19
      : labelData === "Retries (3-5)"
      ? 2
      : day - (day > 5 ? 3 : 1);

  return (
    <div className="bg-surface-elevated border border-edge shadow-overlay rounded-xl px-4 py-3">
      <h2 className="text-[12px] font-medium mb-2 text-light-gray">
        <span>{data[label]?.day}</span>
      </h2>
      <hr className="border-edge-faint" />
      <h2 className="text-[13px] font-medium mt-2 text-text-secondary">
        Daily :{" "}
        <span className="text-heading-color font-semibold tabular-nums">
          £{Number(data[label]?.daily)?.toFixed(2)}
        </span>
      </h2>
      <h2 className="text-[13px] font-medium mt-1 text-text-secondary">
        Cumulative :{" "}
        <span className="text-heading-color font-semibold tabular-nums">
          {Number(data[label]?.cumulative)}%
        </span>
      </h2>
    </div>
  );
};
